import { doNotHasText, validateField, validateFieldNumber } from 'utils/validate.util'
import { z } from 'zod'
import { addCustomIssue } from 'utils/schema.util'

// v1
export const addressSchema = z
  .object({
    id: z.number().nullish(),
    bairro: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    bairroId: z.preprocess((field) => {
      return validateFieldNumber(field)
    }, z.string()),
    cep: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    cidade: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    cidadeId: z.preprocess((field) => {
      return validateFieldNumber(field)
    }, z.string()),
    complemento: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    ddd: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    gia: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    ibge: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    logradouro: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    numero: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    siafi: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    uf: z.preprocess((field) => {
      return validateField(field)
    }, z.string()),
    ufId: z.preprocess((field) => {
      return validateFieldNumber(field)
    }, z.string()),
    pontoReferencia: z.string().nullish(),
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string, message: string) => addCustomIssue(ctx, [field], message)

    if (!doNotHasText(fields.cep) && doNotHasText(fields.bairroId)) {
      addIssue('bairroId', 'Bairro não preenchido.')
    }

    if (!doNotHasText(fields.cep) && doNotHasText(fields.ufId)) {
      addIssue('ufId', 'UF não preenchido.')
    }

    if (!doNotHasText(fields.cep) && doNotHasText(fields.cidadeId)) {
      addIssue('cidadeId', 'Cidade não preenchido.')
    }

    if (!doNotHasText(fields.cep) && doNotHasText(fields.logradouro)) {
      addIssue('logradouro', 'Logradouro não preenchido.')
    }

    if (
      (!doNotHasText(fields.bairroId) ||
        !doNotHasText(fields.cep) ||
        !doNotHasText(fields.cidadeId) ||
        !doNotHasText(fields.ufId) ||
        !doNotHasText(fields.complemento) ||
        !doNotHasText(fields.numero)) &&
      doNotHasText(fields.logradouro)
    ) {
      addIssue('logradouro', 'Logradouro não preenchido.')
    }
  })

export const personAddressSchema = z.object({
  id: z.number().nullish(),
  endereco: addressSchema,
  enderecoPrincipal: z.string(),
})

export type PersonAddressType = z.infer<typeof personAddressSchema>

export type AddressType = z.infer<typeof addressSchema>

export const addressInit: AddressType = {
  id: null,
  bairro: '',
  bairroId: '0',
  cep: '',
  cidade: '',
  cidadeId: '0',
  complemento: '',
  ddd: '',
  gia: '',
  ibge: '',
  logradouro: '',
  numero: '',
  siafi: '',
  uf: '',
  ufId: '0',
  pontoReferencia: '',
}

export const personAddressInit: PersonAddressType = {
  endereco: addressInit,
  enderecoPrincipal: 'N',
}

export const NeighborhoodSchema = z.object({
  id: z.number().nullish(),
  nome: z.string().nullish(),
})

export const CitySchema = z.object({
  id: z.number().nullish(),
  nome: z.string().nullish(),
  uf: z.object({
    id: z.number().nullish(),
    nome: z.string().nullish(),
  }),
})

//v2
export const addressV2Schema = z
  .object({
    id: z.number().nullish(),
    cep: z.string().nullish(),
    bairro: NeighborhoodSchema,
    cidade: CitySchema,
    logradouro: z.string().nullish(),
    numero: z.string().nullish(),
    complemento: z.string().nullish(),
    pontoReferencia: z.string().nullish(),
  })
  .superRefine((fields, ctx) => {
    const addIssue = (field: string, message: string) => addCustomIssue(ctx, [field], message)

    if (!doNotHasText(fields.cep) && doNotHasText(fields.bairro.id)) {
      addIssue('bairro.id', 'Bairro não preenchido.')
    }

    if (!doNotHasText(fields.cep) && doNotHasText(fields.cidade.uf.id)) {
      addIssue('cidade.uf.id', 'UF não preenchido.')
    }

    if (!doNotHasText(fields.cep) && doNotHasText(fields.cidade.id)) {
      addIssue('cidade.id', 'Cidade não preenchido.')
    }

    if (!doNotHasText(fields.cep) && doNotHasText(fields.logradouro)) {
      addIssue('logradouro', 'Logradouro não preenchido.')
    }

    if (
      (!doNotHasText(fields.bairro.id) ||
        !doNotHasText(fields.cep) ||
        !doNotHasText(fields.cidade.id) ||
        !doNotHasText(fields.cidade.uf.id) ||
        !doNotHasText(fields.complemento) ||
        !doNotHasText(fields.numero)) &&
      doNotHasText(fields.logradouro)
    ) {
      addIssue('logradouro', 'Logradouro não preenchido.')
    }
  })

export const naturalPersonAddressSchema = z.object({
  id: z.number().nullish(),
  endereco: addressV2Schema,
  enderecoPrincipal: z.boolean(),
})

export type AddressV2Type = z.infer<typeof addressV2Schema>
export type NaturalPersonAddressType = z.infer<typeof naturalPersonAddressSchema>

export const AddressV2Init: AddressV2Type = {
  id: null,
  cep: null,
  complemento: null,
  logradouro: null,
  numero: null,
  pontoReferencia: null,
  bairro: {
    id: null,
    nome: null,
  },
  cidade: {
    id: null,
    nome: null,
    uf: {
      id: null,
      nome: null,
    },
  },
}

export const NaturalPersonAddressInit: NaturalPersonAddressType = {
  endereco: AddressV2Init,
  enderecoPrincipal: false,
}
